import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Phone, ShieldCheck, ShoppingBag } from "lucide-react";
import { SEO } from "../components/SEO";
import { useCart } from "../context/CartContext";
import { api, fmt } from "../lib/api";
import { ecwidConfigured, hideEcwidBranding, initCheckoutWidget, loadEcwid, syncCartToEcwid } from "../lib/ecwid";
import { SITE } from "../config/site";

export default function CheckoutPage() {
  const { items, subtotal, openVerification } = useCart();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("syncing");
  const [failures, setFailures] = useState([]);
  const widgetRef = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Gate the page itself: no verified ID on file → force the modal;
    // dismissing it redirects to /shop. Kills the persisted-Ecwid-cart bypass.
    const email = localStorage.getItem("ccdc_verify_email");
    if (!email) {
      openVerification(() => window.location.reload(), () => navigate("/shop"));
      return;
    }
    if (!ecwidConfigured) {
      setPhase("unconfigured");
      return;
    }
    if (items.length === 0) {
      setPhase("empty");
      return;
    }
    let disconnect = () => {};
    (async () => {
      // Re-validate the saved verification against the server (localStorage lies).
      try {
        const s = await api.verificationStatus(email);
        if (!s.verified) {
          localStorage.removeItem("ccdc_age_verify");
          localStorage.removeItem("ccdc_verify_email");
          openVerification(() => window.location.reload(), () => navigate("/shop"));
          return;
        }
      } catch (_) {
        /* network down — don't block; the sweep flags missing IDs */
      }
      const ok = await loadEcwid();
      if (!ok) return setPhase("error");
      // Pre-fill the email from 21+ verification so checkout asks once, not twice.
      if (email && window.Ecwid?.Cart?.setCustomerEmail) {
        try { window.Ecwid.Cart.setCustomerEmail(email); } catch (_) {}
      }
      // Mirror the custom cart into Ecwid's browser cart first (verified via a
      // final Cart.get — Ecwid callbacks are unreliable), THEN open the widget
      // on the cart page so it renders with items present.
      const { failures: failed } = await syncCartToEcwid(items);
      setFailures(failed);
      initCheckoutWidget("ecwid-checkout");
      disconnect = hideEcwidBranding(widgetRef.current);
      setPhase("ready");
    })().catch(() => setPhase("error"));
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-10 lg:py-14">
      <SEO title="Secure Checkout" path="/checkout" description="Complete your CannaCheap DC order — secure checkout, fast DC delivery or express pickup." />

      <div className="max-w-2xl">
        <h1 className="text-4xl font-black text-brand-forest sm:text-5xl">Checkout</h1>
        <p className="mt-3 flex items-center gap-2 text-sm text-brand-ink/60">
          <ShieldCheck className="h-4 w-4 text-brand-pine" />
          Payment & order processing is handled end-to-end by our secure store platform.
        </p>
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <aside className="lg:col-span-4" aria-label="Order summary">
          <div className="rounded-2xl border border-brand-line bg-white p-6" data-testid="order-summary">
            <h2 className="font-display text-lg font-black text-brand-forest">Order Summary</h2>
            <ul className="mt-4 divide-y divide-brand-line/70">
              {items.map((item) => (
                <li key={item.key} className="flex gap-3 py-3" data-testid={`summary-item-${item.id}`}>
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg">
                    {item.image && <img src={item.image} alt={item.name} className="img-blend h-full w-full object-cover" loading="lazy" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold leading-snug">{item.name}</p>
                    {item.optionsDisplay?.length > 0 && (
                      <p className="text-xs text-brand-ink/55">{item.optionsDisplay.join(" · ")}</p>
                    )}
                    <p className="text-xs text-brand-ink/55">Qty {item.qty}</p>
                  </div>
                  <p className="text-sm font-black text-brand-pine">{fmt(item.price * item.qty)}</p>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex items-center justify-between border-t border-brand-line pt-4">
              <span className="text-sm text-brand-ink/60">Subtotal</span>
              <span className="font-display text-xl font-black text-brand-forest" data-testid="summary-subtotal">{fmt(subtotal)}</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-brand-ink/50">
              Minimum order {fmt(SITE.orderMinimum)} · free DC delivery over {fmt(SITE.freeDeliveryMinimum)} with
              code DC80 · taxes & fees confirmed at checkout. {SITE.ageLine}.
            </p>
            {subtotal < SITE.orderMinimum && (
              <p data-testid="checkout-minimum-notice" className="mt-3 rounded-xl border border-brand-gold/50 bg-brand-gold/10 px-4 py-3 text-xs font-medium text-brand-golddeep">
                You're below the {fmt(SITE.orderMinimum)} order minimum — add more items before checkout.
              </p>
            )}
          </div>
          {failures.length > 0 && (
            <p className="mt-4 rounded-xl border border-brand-gold/50 bg-brand-gold/10 px-4 py-3 text-xs leading-relaxed text-brand-golddeep" data-testid="checkout-sync-notice">
              Some items need ID verification — you'll be asked to confirm your 21+ ID inside the secure
              checkout before your order is placed.
            </p>
          )}
        </aside>

        <div className="lg:col-span-8">
          <div ref={widgetRef} className="min-h-[28rem] rounded-2xl border border-brand-line bg-white p-4 sm:p-8" data-testid="ecwid-checkout-frame">
            {phase === "syncing" && (
              <div className="flex h-96 flex-col items-center justify-center gap-4 text-brand-ink/60" data-testid="checkout-loading">
                <Loader2 className="h-8 w-8 animate-spin text-brand-pine" />
                <p className="text-sm">Preparing your secure checkout…</p>
              </div>
            )}
            {phase === "empty" && (
              <div className="flex h-96 flex-col items-center justify-center gap-4 text-center" data-testid="checkout-empty">
                <ShoppingBag className="h-10 w-10 text-brand-ink/25" />
                <p className="font-display text-xl font-bold text-brand-forest">Your cart is empty</p>
                <Link to="/shop" data-testid="checkout-empty-shop" className="rounded-full bg-brand-pine px-8 py-3.5 text-sm font-bold text-brand-cream">
                  Browse the Menu
                </Link>
              </div>
            )}
            {phase === "error" && (
              <div className="flex h-96 flex-col items-center justify-center gap-4 text-center" data-testid="checkout-error">
                <p className="font-display text-xl font-bold text-brand-forest">Checkout couldn't load</p>
                <p className="max-w-sm text-sm text-brand-ink/60">
                  Check your connection and refresh — or call us and we'll take your order by phone.
                </p>
                <a href={SITE.phoneHref} data-testid="checkout-call" className="flex items-center gap-2 rounded-full bg-brand-pine px-8 py-3.5 text-sm font-bold text-brand-cream">
                  <Phone className="h-4 w-4" /> {SITE.phone}
                </a>
              </div>
            )}
            {phase === "unconfigured" && (
              <div className="flex h-96 flex-col items-center justify-center gap-4 text-center" data-testid="checkout-unconfigured">
                <p className="font-display text-xl font-bold text-brand-forest">Online checkout is being finalized</p>
                <p className="max-w-sm text-sm text-brand-ink/60">
                  Your order summary is saved on the left. Call or text us and we'll complete your order right away.
                </p>
                <a href={SITE.phoneHref} data-testid="checkout-call-alt" className="flex items-center gap-2 rounded-full bg-brand-pine px-8 py-3.5 text-sm font-bold text-brand-cream">
                  <Phone className="h-4 w-4" /> {SITE.phone}
                </a>
              </div>
            )}
            <div id="ecwid-checkout" data-testid="ecwid-widget" />
          </div>
        </div>
      </div>
    </div>
  );
}
